import { msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Box,
  Paper, Skeleton,
  Typography,
  useTheme } from
'@mui/material/';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Three different icons to choose from
// import HubIcon from '@mui/icons-material/Hub'
// import WorkspacesIcon from '@mui/icons-material/Workspaces'
import DesktopNav from '../components/desktop/DesktopNav';
import DesktopToolbar from "../components/desktop/DesktopToolbar";
import SvgCustomIcon from "../components/icons/SvgCustomIcon";
import { Snack } from '../components/Snack';
import CrashCsvImportPanel from '../components/admin/crash-csv-import-panel';
import ConfTable from '../components/tables/ConfigsTable';
import OrganizationsTable from '../components/tables/OrganizationsTable';
import UsersTable from '../components/tables/UsersTable';
import { NAV_LAYOUT_MARGIN_START } from '../consts/nav-layout-margin';
import { NewApiBaseUrl } from "../consts/new-api-base-url";
import useHttp from '../hooks/useHttp';
import useHttpApiNew from "../hooks/useHttpApiNew";
import useLogout from '../hooks/useLogout';
import { useActionLogs } from '../store/actionLogsContext';
import { interpolateOrgs } from '../utils';
import { syncOfflineUserDirectory } from '../offline/offline-data-sync';
import { removeOfflineUserByUsername } from '../offline/offline-users-store';
import { buildConfigLocalizedUrl, getConfigLocaleHeaders } from '../utils/config-localization';

// eslint-disable-next-line no-restricted-globals
// const getUsersUrl = new URL('/api/users', location)
const getUsersListUrl = new URL(`${NewApiBaseUrl}/Auth/GetList`);

// eslint-disable-next-line no-restricted-globals
// const postUserUrl = new URL('/api/user', location)
const modifyUserUrl = new URL(`${NewApiBaseUrl}/Auth/Modify`);
const modifyUserPasswordUrl = new URL(`${NewApiBaseUrl}/Auth/ChangePassword`);
// eslint-disable-next-line no-restricted-globals
const structureUrl = new URL('/api/structure', location);
const newStructureUrl = new URL(`${NewApiBaseUrl}/Structure`);
// eslint-disable-next-line no-restricted-globals
// const initUrl = new URL('/api/init', location)
// eslint-disable-next-line no-restricted-globals
const initUrl = new URL(`${NewApiBaseUrl}/Auth/Init`);
const organizationsGetListUrl = new URL(`${NewApiBaseUrl}/Organization`);
// eslint-disable-next-line no-restricted-globals
const addOrganizationUrl = new URL(`${NewApiBaseUrl}/Organization/Add`);
// eslint-disable-next-line no-restricted-globals
const modifyOrganizationUrl = new URL(`${NewApiBaseUrl}/Organization/Modify`);
// eslint-disable-next-line no-restricted-globals
const deleteOrganizationUrl = new URL(`${NewApiBaseUrl}/Organization`);

export default function Admin({ position, isMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const state = location.state || {};
  const persistedSession = useMemo(() => {
    try {
      const raw = localStorage.getItem('adams_user');
      const u = raw ? JSON.parse(raw) : null;
      return {
        token: u?.token || localStorage.getItem('token') || null,
        init: u?.init || {}
      };
    } catch {
      return {
        token: localStorage.getItem('token') || null,
        init: {}
      };
    }
  }, []);
  const token = state?.token || persistedSession.token;
  const init = state?.init || persistedSession.init || {};
  const { configuration } = init;
  const theme = useTheme();
  const { _ } = useLingui();
  /** Allineato al drawer desktop (`--adams-nav-drawer-width`), non a `theme.mixins.drawerWidth` fisso. */
  const navContentMarginStart = NAV_LAYOUT_MARGIN_START;
  const { isLoading, error, sendRequest, setError } = useHttp();
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState({});
  const [message, setMessage] = useState({});
  const [showSub, setShowSub] = useState('users');
  const [organizations, setOrganizations] = useState([]);
  const { request } = useHttpApiNew();
  const { fetchLogs } = useActionLogs();

  const TableSkel = () => // ? <CircularProgress sx={{ mt: 2 }} />
  <>
            <Skeleton width='100%' sx={{ mt: 2, fontSize: '2rem' }} />
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} width='100%' />)}
            {/* <Skeleton width='100%' sx={{ fontSize: '2rem' }} /> */}
        </>;


  const iconSizeStyle = {
    height: 20,
    width: 20
  };

  const subPanelElements = [
  {
    label: t`Users management`,
    icon: <SvgCustomIcon name={'usersManagement'} />,
    type: 'button',
    onMobile: false,
    access: [1, 2, 3],
    active: showSub === 'users',
    action: () => setShowSub('users')
  },
  {
    label: t`Organizations management`,
    icon: <SvgCustomIcon name={'cases'} />,
    type: 'button',
    onMobile: false,
    access: [1],
    active: showSub === 'organizations',
    action: () => setShowSub('organizations')

  },
  {
    label: t`Form configuration`,
    icon: <SvgCustomIcon name={'settings'} />,
    type: 'button',
    onMobile: false,
    access: [1],
    active: showSub === 'configuration',
    action: () => setShowSub('configuration')
  },
  {
    label: t`CSV crash import`,
    icon: <SvgCustomIcon name={'fileExport'} />,
    type: 'button',
    onMobile: false,
    access: [2, 3],
    active: showSub === 'csvImport',
    action: () => setShowSub('csvImport')
  }];


  const getOrganizations = useCallback(async () => {
    const response = await request(organizationsGetListUrl, 'GET', null, { Authorization: `Bearer ${token}` });

    if (response.responseAnyError) return;
    setOrganizations(response.responseData);
  }, [token, request]);


  const getUsers = useCallback(async () => {

    const response = await request(
      getUsersListUrl,
      'GET',
      null,
      { Authorization: `Bearer ${token}` }
    );
    if (response.responseAnyError) return;
    setUsers(response.responseData);
  },
  [token, init, request]
  );

  useEffect(() => {
    if (!token) return logout();
    if (isMobile) return navigate('/home', { state: { token } });
    getUsers();
    getOrganizations();
  }, [isMobile, navigate, token, logout, getUsers, getOrganizations]);

  const modifyUserPassword = async (userOrPayload, newPasswordMaybe) => {
    const user = userOrPayload?.selectedUser ?? userOrPayload;
    const newPassword = userOrPayload?.password ?? newPasswordMaybe;


    const response = await request(modifyUserPasswordUrl, 'PATCH', { id: user.id, newPassword: newPassword });
    if (response.responseAnyError) return;
    await getUsers();
    fetchLogs();
    const username = user?.username != null ? String(user.username).trim() : '';
    if (username) {
      removeOfflineUserByUsername(username).catch(() => {});
    }
    if (typeof navigator !== 'undefined' && navigator.onLine && token) {
      syncOfflineUserDirectory(token).catch(() => {});
    }
    setMessage((old) => ({ ...old, user: _(msg`User password successfully modified!`) }));
  };

  const modifyUser = async (data) => {

    const response = await request(
      modifyUserUrl,
      'PATCH',
      data,
      { Authorization: `Bearer ${token}` }
    );
    if (response.responseAnyError) return;
    await getUsers();
    fetchLogs();
    if (typeof navigator !== 'undefined' && navigator.onLine && token) {
      syncOfflineUserDirectory(token).catch(() => {});
    }
    setMessage((old) => ({ ...old, user: _(msg`User successfully modified!`) }));
    /*
    sendRequest({
        token
        , url: postUserUrl
        , httpBody: data
        , httpMethod: 'POST'
        , manageResData: res => {
            if (res.result.includes('success')) {
                getUsers()
                setMessage(old => ({...old, user: _(msg`User successfully modified!`)}))
            }
        }
    })
     */
  };

  const addOrModifyOrganization = async (data) => {
    let url = modifyOrganizationUrl;
    let message = _(msg`Organization successfully modified!`);
    if (data.isNew) {
      url = addOrganizationUrl;
      message = _(msg`Organization successfully added!`);
    }
    const body = {
      ...(data.isNew ? {} : { id: data.id }),
      basicOrganization: data.basic_id,
      description: data.description ?? '',
      short_description: data.short_description ?? ''
    };
    const response = await request(
      url,
      'POST',
      body,
      { Authorization: `Bearer ${token}` }
    );
    if (response.responseAnyError) return;
    getOrganizations();
    fetchLogs();
    setMessage((old) => ({ ...old, organization: message }));
  };

  useEffect(() => {
    let config = {};
    Object.keys(configuration).
    sort((a, b) => configuration[a].position - configuration[b].position).
    forEach((key) => {
      config = {
        ...config,
        [key]: configuration[key].elements.
        map((element, i) => ({ ...element, id: i + 1, section: key }))
      };
    });

    setConfig(config);
  }, [configuration]);

  const deleteOrganization = async (id) => {
    const successMessage = _(msg`Organization successfully deleted!`);
    const deleteUrlWithParam = deleteOrganizationUrl + `/${id}`;
    const response = await request(deleteUrlWithParam, 'DELETE', null, { Authorization: `Bearer ${token}` });
    if (!response || response.responseAnyError) {
      const errText =
      response &&
      typeof response.responseMessage === 'string' &&
      response.responseMessage.trim() ?
      response.responseMessage.trim() :
      _(msg`Could not delete organization.`);
      setError(errText);
      return false;
    }
    setError(null);
    await getOrganizations();
    setMessage((old) => ({ ...old, organization: successMessage }));
    return true;
    /*
    fetch(deleteUrlWithParam, {
        method: 'DELETE',
        headers: {Authorization: `Bearer ${token}`},
    }).then(res => res.json()).then(response => {
        if (response.responseAnyError) return;
        getOrganizations()
        setMessage(old => ({...old, organization: message}))
    })
     */
  };

  const modifyField = async (label, data) => {
    let body;
    const group = data.section;
    if (data.action && data.action === 'delete') {
      body = { action: 'delete', group, label };
    } else if (data.isNew) {
      body = { action: 'add', group, data, label };
    } else {
      body = { action: 'update', group, data, label };
    }
    const structureResponse = await request(newStructureUrl, 'POST', body, { Authorization: `Bearer ${token}` });

    if (structureResponse.responseAnyError) return;
    if (structureResponse.responseData) {
      const initResponse = await request(
        buildConfigLocalizedUrl(initUrl),
        'GET',
        null,
        getConfigLocaleHeaders({ Authorization: `Bearer ${token}` }),
      );
      if (initResponse.responseAnyError) return;

      let { error: initError, error_code, result, organizations, ...init } = initResponse.responseData; // { configuration, defaults, user }
      init = { ...init, organizations: interpolateOrgs(organizations) };
      navigate('.', { state: { token, init } });
      setMessage((old) => ({ ...old, [group]: _(msg`Field successfully modified!`) }));
    }
  };

  const handleResetMessage = () => setMessage({});

  return (
    <Box component="main">
            <DesktopNav position={position} subPanelElements={subPanelElements} />
            <DesktopToolbar />
            <Box sx={{ p: 2 }}>
                <Box sx={{
          marginInlineStart: navContentMarginStart,
          p: 1,
          borderBottom: `1px solid ${theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'}`
        }}>
                    <Typography sx={{
            color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
            fontWeight: 'bold',
            fontSize: 24
          }}>
                        {showSub === 'users' ?
            _(msg`Users list`) :
            showSub === 'organizations' ?
            _(msg`Organizations management`) :
            showSub === 'csvImport' ?
            _(msg`CSV crash import`) :
            _(msg`Form configuration`)}
                    </Typography>
                </Box>

                <Box
          pl={2}
          pr={2}
          display='flex'
          flexDirection='column'
          alignItems='stretch'
          sx={{
            marginInlineStart: navContentMarginStart,
            '& .overload-theme--header': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText
            }
          }}>
          
                    {isLoading ?
          <TableSkel /> :
          <>
                            {
            {
              users:
              <>
                                            <Paper sx={{ width: '100%', marginTop: 2 }}>
                                                <UsersTable modifyUserPassword={modifyUserPassword}
                  usersRows={users}
                  modifyUser={modifyUser} error={error} />
                                            </Paper>
                                        </>,
              organizations:
              <>
                                            <Paper sx={{ width: '100%', marginTop: 2 }}>
                                                <OrganizationsTable
                    organizations={organizations}
                    addOrModifyOrganization={addOrModifyOrganization}
                    deleteOrganization={deleteOrganization} />
                  
                                            </Paper>
                                        </>,
              csvImport:
              <>
                                            <CrashCsvImportPanel bearerToken={token} />
                                        </>,
              configuration:
              <>


                                            {Object.keys(config).map((section, i) =>
                <Fragment key={section}>
                                                    {isLoading ?
                  <TableSkel /> :
                  <>

                                                            <Box sx={{
                      width: '100%',
                      mb: i === Object.keys(config).length - 1 ? 4 : 0,
                      marginTop: 3
                    }}>
                                                                <ConfTable
                        sections={Object.keys(config)}
                        section={section}
                        sectionRows={config[section]}
                        modifyField={modifyField}
                        message={message[section]}
                        handleResetMessage={handleResetMessage}
                        error={error} />
                      
                                                            </Box>
                                                        </>}
                                                </Fragment>
                )}
                                        </>
            }[showSub]
            }
                        </>}
                    <Snack
            message={message.user || message.organization}
            setMessage={setMessage}
            error={error}
            setError={setError} />
          
                    {/* {(message.user || message.organization) &&
            <Alert sx={{ mr: 'auto', ml: 'auto', mt: 2, mb: 2 }} severity='success'>
            {message.user || message.organization}
            </Alert>}
            {error &&
            <Alert sx={{ mr: 'auto', ml: 'auto', mt: 2, mb: 2 }} severity="error">
            <Trans>There was an error: {error}.</Trans>
            </Alert>} */}
                </Box>
            </Box>
        </Box>);

}